import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  ToggleButton,
  ToggleButtonGroup,
  Tabs,
  Tab,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import BarChartIcon from "@mui/icons-material/BarChart";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import dayjs, { Dayjs } from "dayjs";
import {
  DailyInsightType,
  CourtTypes,
} from "../../types/generalTypes";
import { useInsights } from "../../hooks/useInsights";
import { getCourtsByCompany } from "../../api/CourtAPI";
import { getCompanyData } from "../../utils/generalUtil";
import LoadingSpinner from "../loadingSpinner/LoadingSpinner";
import ModalityInsights from "./ModalityInsights";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

type ChartType = "bar" | "line";
type InsightViewType = "courts" | "modalities";

const COURT_COLORS = [
  "#226FE2",
  "#E91E63",
  "#9C27B0",
  "#FF9800",
  "#4CAF50",
  "#00BCD4",
  "#FF5722",
  "#795548",
];

const getCourtColor = (courtId: number): string => {
  return COURT_COLORS[courtId % COURT_COLORS.length];
};

const CompanyInsights = () => {
  const [viewType, setViewType] = useState<InsightViewType>("courts");
  const [startDate, setStartDate] = useState<Dayjs>(dayjs().startOf("month"));
  const [endDate, setEndDate] = useState<Dayjs>(dayjs().endOf("month"));
  const [selectedCourts, setSelectedCourts] = useState<number[]>([]);
  const [courts, setCourts] = useState<CourtTypes[]>([]);
  const [insights, setInsights] = useState<DailyInsightType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [chartType, setChartType] = useState<ChartType>("bar");

  const { fetchInsights } = useInsights({ setInsights, setIsLoading });

  useEffect(() => {
    const loadCourts = async () => {
      try {
        const companyData = getCompanyData();
        const courtsData = await getCourtsByCompany(
          companyData?.companyId || 0,
        );
        setCourts(courtsData);
        setSelectedCourts(courtsData.map((c) => Number(c.courtId)));
      } catch (error) {
        console.error("Failed to load courts:", error);
      }
    };

    loadCourts();
  }, []);

  const handleFetchInsights = useCallback(async () => {
    if (selectedCourts.length === 0) {
      alert("Selecione pelo menos uma quadra");
      return;
    }

    const companyData = getCompanyData();

    await fetchInsights({
      companyId: companyData?.companyId || 0,
      courtIds: selectedCourts,
      startDate: startDate.format("YYYY-MM-DD"),
      endDate: endDate.format("YYYY-MM-DD"),
    });
    setHasLoaded(true);
  }, [selectedCourts, startDate, endDate, fetchInsights, setHasLoaded]);

  useEffect(() => {
    if (selectedCourts.length > 0 && !hasLoaded) {
      handleFetchInsights();
      setHasLoaded(true);
    }
  }, [selectedCourts, hasLoaded, handleFetchInsights]);

  const handleChartTypeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newChartType: ChartType,
  ) => {
    if (newChartType !== null) {
      setChartType(newChartType);
    }
  };

  const getTotalReservations = () => {
    return insights.reduce((sum, day) => sum + day.totalReservations, 0);
  };

  const getAveragePerDay = () => {
    if (insights.length === 0) return 0;
    return (getTotalReservations() / insights.length).toFixed(1);
  };

  const prepareDailyChartData = () => {
    return insights.map((day) => ({
      date: dayjs(day.date).format("DD/MM"),
      total: day.totalReservations,
    }));
  };

  const prepareCourtComparisonData = () => {
    const courtTotals = new Map<string, number>();

    insights.forEach((day) => {
      day.courts.forEach((court) => {
        const current = courtTotals.get(court.courtName) || 0;
        courtTotals.set(court.courtName, current + court.reservationCount);
      });
    });

    const courtNames = Array.from(courtTotals.keys());

    return courtNames.map((name) => {
      const obj: any = { name };
      courtNames.forEach((courtName) => {
        obj[courtName] =
          courtName === name ? courtTotals.get(courtName) || 0 : 0;
      });
      return obj;
    });
  };

  const prepareMultipleCourtsData = () => {
    return insights.map((day) => {
      const data: any = {
        date: dayjs(day.date).format("DD/MM"),
      };

      day.courts.forEach((court) => {
        data[court.courtName] = court.reservationCount;
      });

      return data;
    });
  };

  const renderDailyChart = () => {
    const data = prepareDailyChartData();

    if (chartType === "bar") {
      return (
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis dataKey="date" stroke="#666" tick={{ fill: "#666" }} />
          <YAxis stroke="#666" tick={{ fill: "#666" }} />
          <Tooltip />
          <Legend />
          {selectedCourts.map((courtId) => {
            const court = courts.find((c) => Number(c.courtId) === courtId);
            return (
              <Bar
                key={courtId}
                dataKey={(item: any) => {
                  const courtData = insights
                    .find((d) => dayjs(d.date).format("DD/MM") === item.date)
                    ?.courts.find((c) => Number(c.courtId) === courtId);
                  return courtData?.reservationCount || 0;
                }}
                fill={getCourtColor(Number(courtId))}
                name={court?.name}
              />
            );
          })}
        </BarChart>
      );
    }

    return (
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
        <XAxis dataKey="date" stroke="#666" tick={{ fill: "#666" }} />
        <YAxis stroke="#666" tick={{ fill: "#666" }} />
        <Tooltip />
        <Legend />
        {selectedCourts.map((courtId) => {
          const court = courts.find((c) => Number(c.courtId) === courtId);
          const color = getCourtColor(Number(courtId));
          return (
            <Line
              key={courtId}
              type="monotone"
              dataKey={(item: any) => {
                const courtData = insights
                  .find((d) => dayjs(d.date).format("DD/MM") === item.date)
                  ?.courts.find((c) => Number(c.courtId) === courtId);
                return courtData?.reservationCount || 0;
              }}
              stroke={color}
              strokeWidth={2}
              name={court?.name}
              dot={{ fill: color, r: 4 }}
              activeDot={{ r: 6 }}
            />
          );
        })}
      </LineChart>
    );
  };

  const renderCourtComparisonChart = () => {
    const data = prepareCourtComparisonData();

    if (chartType === "bar") {
      return (
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis dataKey="name" stroke="#666" tick={{ fill: "#666" }} />
          <YAxis stroke="#666" tick={{ fill: "#666" }} />
          <Tooltip />
          <Legend />
          {data.map((item, index) => {
            const court = courts.find((c) => c.name === item.name);
            const courtId = Number(court?.courtId) || index;
            const color = getCourtColor(courtId);
            return (
              <Bar
                key={item.name}
                dataKey={item.name}
                fill={color}
                name={item.name}
              />
            );
          })}
        </BarChart>
      );
    }

    return (
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
        <XAxis dataKey="name" stroke="#666" tick={{ fill: "#666" }} />
        <YAxis stroke="#666" tick={{ fill: "#666" }} />
        <Tooltip />
        <Legend />
        {data.map((item, index) => {
          const court = courts.find((c) => c.name === item.name);
          const courtId = Number(court?.courtId) || index;
          const color = getCourtColor(courtId);
          return (
            <Line
              key={item.name}
              type="monotone"
              dataKey={item.name}
              stroke={color}
              strokeWidth={2}
              name={item.name}
              dot={{ fill: color, r: 4 }}
              activeDot={{ r: 6 }}
            />
          );
        })}
      </LineChart>
    );
  };

  const renderMultipleCourtsChart = () => {
    const data = prepareMultipleCourtsData();

    if (chartType === "bar") {
      return (
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis dataKey="date" stroke="#666" tick={{ fill: "#666" }} />
          <YAxis stroke="#666" tick={{ fill: "#666" }} />
          <Tooltip />
          <Legend />
          {selectedCourts.map((courtId) => {
            const court = courts.find((c) => Number(c.courtId) === courtId);
            return (
              <Bar
                key={courtId}
                dataKey={court?.name}
                fill={getCourtColor(Number(courtId))}
                name={court?.name}
              />
            );
          })}
        </BarChart>
      );
    }

    return (
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
        <XAxis dataKey="date" stroke="#666" tick={{ fill: "#666" }} />
        <YAxis stroke="#666" tick={{ fill: "#666" }} />
        <Tooltip />
        <Legend />
        {selectedCourts.map((courtId) => {
          const court = courts.find((c) => Number(c.courtId) === courtId);
          const color = getCourtColor(Number(courtId));
          return (
            <Line
              key={courtId}
              type="monotone"
              dataKey={court?.name}
              stroke={color}
              strokeWidth={2}
              name={court?.name}
              dot={{ fill: color, r: 4 }}
              activeDot={{ r: 6 }}
            />
          );
        })}
      </LineChart>
    );
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <LoadingSpinner />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" mb={3}>
        Insights
      </Typography>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={viewType}
          onChange={(_, newValue) => setViewType(newValue)}
          indicatorColor="primary"
          textColor="primary"
          centered
        >
          <Tab label="Por Quadra" value="courts" />
          <Tab label="Por Modalidade" value="modalities" />
        </Tabs>
      </Paper>

      {viewType === "modalities" ? (
        <ModalityInsights />
      ) : (
        <>
          <Box
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            mb={3}
          >
            <ToggleButtonGroup
              value={chartType}
              exclusive
              onChange={handleChartTypeChange}
              size="small"
            >
              <ToggleButton value="bar" aria-label="bar chart">
                <BarChartIcon />
              </ToggleButton>
              <ToggleButton value="line" aria-label="line chart">
                <ShowChartIcon />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Paper sx={{ p: 3, mb: 3 }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Data Início"
                    value={startDate}
                    onChange={(newValue) => newValue && setStartDate(newValue)}
                    maxDate={endDate}
                    slotProps={{
                      textField: { fullWidth: true, size: "small" },
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Data Fim"
                    value={endDate}
                    onChange={(newValue) => newValue && setEndDate(newValue)}
                    minDate={startDate}
                    slotProps={{
                      textField: { fullWidth: true, size: "small" },
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Quadras</InputLabel>
                  <Select
                    multiple
                    value={selectedCourts}
                    label="Quadras"
                    onChange={(e) =>
                      setSelectedCourts(e.target.value as number[])
                    }
                    renderValue={(selected) =>
                      `${selected.length} quadra(s) selecionada(s)`
                    }
                  >
                    {courts.map((court) => (
                      <MenuItem
                        key={court.courtId}
                        value={Number(court.courtId)}
                      >
                        <Checkbox
                          checked={selectedCourts.includes(
                            Number(court.courtId),
                          )}
                        />
                        <ListItemText primary={court.name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleFetchInsights}
                  disabled={selectedCourts.length === 0}
                >
                  Buscar
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {insights.length > 0 && (
            <>
              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  Legenda das Quadras
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={2}>
                  {selectedCourts.map((courtId) => {
                    const court = courts.find(
                      (c) => Number(c.courtId) === courtId,
                    );
                    const color = getCourtColor(Number(courtId));
                    return (
                      <Box
                        key={courtId}
                        display="flex"
                        alignItems="center"
                        gap={1}
                      >
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: 1,
                            backgroundColor: color,
                          }}
                        />
                        <Typography variant="body2">{court?.name}</Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Paper>

              <Grid container spacing={3} mb={3}>
                <Grid item xs={12} sm={6} md={4}>
                  <Card>
                    <CardContent>
                      <Typography
                        color="textSecondary"
                        gutterBottom
                        fontSize={14}
                      >
                        Total de Reservas
                      </Typography>
                      <Typography variant="h4" fontWeight="bold">
                        {getTotalReservations()}
                      </Typography>
                      <Typography color="textSecondary" fontSize={12} mt={1}>
                        {startDate.format("MMM DD")} -{" "}
                        {endDate.format("MMM DD")}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Card>
                    <CardContent>
                      <Typography
                        color="textSecondary"
                        gutterBottom
                        fontSize={14}
                      >
                        Média por Dia
                      </Typography>
                      <Typography variant="h4" fontWeight="bold">
                        {getAveragePerDay()}
                      </Typography>
                      <Typography color="textSecondary" fontSize={12} mt={1}>
                        Reservas/dia
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" fontWeight="bold" mb={2}>
                        Reservas por Dia
                      </Typography>
                      <ResponsiveContainer width="100%" height={300}>
                        {renderDailyChart()}
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" fontWeight="bold" mb={2}>
                        Total de Reservas por Quadra
                      </Typography>
                      <ResponsiveContainer width="100%" height={300}>
                        {renderCourtComparisonChart()}
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" fontWeight="bold" mb={2}>
                        Reservas por Quadra (Ao longo do tempo)
                      </Typography>
                      <ResponsiveContainer width="100%" height={300}>
                        {renderMultipleCourtsChart()}
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </>
          )}

          {insights.length === 0 && hasLoaded && (
            <Paper sx={{ p: 4, textAlign: "center" }}>
              <Typography color="textSecondary">
                Nenhuma reserva encontrada para o período selecionado
              </Typography>
            </Paper>
          )}
        </>
      )}
    </Box>
  );
};

export default CompanyInsights;
